import { useState } from 'react'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import { now as serverNow } from '~/models/clock'
import { formatDateValue } from '~/models/dateTime'
import {
  type MarketBoardKomaSelection,
  restoreMarketBoardKoma,
  serializeMarketBoardKoma,
} from '~/pages/IntradayBidding/models/marketBoardKoma'

const MARKET_BOARD_KOMA_STORAGE_KEY = 'ebalance-intraday-market-board-koma'

const restore = (deliveryDate: Date): MarketBoardKomaSelection =>
  restoreMarketBoardKoma(
    getLocalStorageItem(MARKET_BOARD_KOMA_STORAGE_KEY),
    deliveryDate,
    serverNow(),
  )

/**
 * 板発注タブで選んだコマ (issue #10107)。 タブの往復・再訪をまたいで保持する。
 * 保持したコマを使うか入札できる先頭のコマ (issue #11101) にするかの規則は
 * [[restoreMarketBoardKoma]]。
 *
 * - 保存するのは利用者がコマを選んだ時だけ。 受渡日の変更で入札できる先頭のコマに
 *   なった値は保存しない (ケース対象日の確定で受渡日が切り替わっただけで、 普段の受渡日で
 *   選んだコマを消さないため。 `usePersistedFilters` の「未操作の既定値は書かない」 と同じ)
 * - 復元の判定 (受渡日の一致・GC 済) は開いた時と受渡日が変わった時だけ行う。
 *   表示中に GC 済になっても選択は動かさない
 *
 * 入札できる先頭のコマは JST 壁時計の GC 時刻で決まる (canonical は `models/deliveryDate`
 * の firstOpenSlot、 非 JST 端末でコマがズレないため #4765)。 時刻はサーバー由来の
 * serverNow から取る。
 */
export function useMarketBoardKoma(
  deliveryDate: Date,
): [number, (koma: number) => void] {
  const [selection, setSelection] = useState(() => restore(deliveryDate))

  // 受渡日が変わった (ヘッダーで変えた / ケース対象日が確定した / 「当日」 のまま 0 時を
  // 跨いだ) 時は、 保存値を新しい受渡日で復元し直す。 render 中に自分の state を直す
  // (props 変化への追随)。
  const deliveryDateKey = formatDateValue(deliveryDate)
  let current = selection
  if (selection.deliveryDate !== deliveryDateKey) {
    current = restore(deliveryDate)
    setSelection(current)
  }

  const setKoma = (koma: number): void => {
    const next = { deliveryDate: deliveryDateKey, koma }
    setSelection(next)
    setLocalStorageItem(
      MARKET_BOARD_KOMA_STORAGE_KEY,
      serializeMarketBoardKoma(next),
    )
  }
  return [current.koma, setKoma]
}
